<template>
  <div class="navigation-wrapper">
    <!-- Mobile overlay -->
    <div class="mobile-overlay" :class="{ 'is-visible': mobileMenuOpen }" @click="closeMobileMenu"></div>

    <!-- Sidebar toggle button -->
    <button
      v-if="!isMobile"
      class="sidebar-toggle-btn"
      :class="{ rotated: barClosed }"
      @click="changeSidebar()"
      aria-label="Toggle sidebar"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 8L10 12L14 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <div class="navigation-sidebar" :class="[sidebarDefault ? '' : barClosed ? 'shrink-sidebar' : 'expand-sidebar', mobileMenuOpen ? 'mobile-open' : '']">
    <ul class="nav sidebar-container">
      <p v-if="!effectiveBarClosed" class="division-title"
          :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']">
          MENU</p>
      <li
        class="nav-item nav-item--has-submenu"
        @mouseenter="openSubmenu($event, 'users')"
        @mouseleave="scheduleCloseSubmenu"
      >
        <RouteLink :route="'users'" :routeLabel="'Clientes'" :barClosed="effectiveBarClosed" @click.native="handleNavClick">
          <Icon icon="document-user" />
        </RouteLink>
      </li>
      <li
        v-if="showFinanceNav"
        class="nav-item nav-item--has-submenu"
        @mouseenter="openSubmenu($event, 'finance')"
        @mouseleave="scheduleCloseSubmenu"
      >
        <RouteLink :route="'finance'" :routeLabel="'Financeiro'" :barClosed="effectiveBarClosed" @click.native="handleNavClick">
          <Icon icon="coin" />
        </RouteLink>
      </li>
      <li
        v-if="showAutonomousNav"
        class="nav-item nav-item--has-submenu"
        @mouseenter="openSubmenu($event, 'autonomos')"
        @mouseleave="scheduleCloseSubmenu"
      >
        <RouteLink
          :route="autonomousRoute"
          :active-routes="['master-autonomous-upsells', 'master-autonomous-self-service']"
          :routeLabel="'Autônomos'"
          :barClosed="effectiveBarClosed"
          @click.native="handleNavClick"
        >
          <Icon icon="coin-upsell" />
        </RouteLink>
      </li>
      <li
        v-if="showCancelamentosNav"
        class="nav-item nav-item--has-submenu"
        @mouseenter="openSubmenu($event, 'cancelamentos')"
        @mouseleave="scheduleCloseSubmenu"
      >
        <RouteLink
          :route="'cancelamentos'"
          :routeLabel="'Cancelamentos'"
          :barClosed="effectiveBarClosed"
          @click.native="handleNavClick"
        >
          <Icon icon="cancelamentos" />
        </RouteLink>
      </li>
      <li v-if="permissions.reservas(user.position)" class="nav-item">
        <RouteLink :route="'reservas'" :routeLabel="'Reservas'" :barClosed="effectiveBarClosed">
          <Icon icon="calendar" />
        </RouteLink>
      </li>
      <li v-if="showAplicativosNav" class="nav-item">
        <RouteLink :route="'aplicativos'" :routeLabel="'Aplicativos'" :barClosed="effectiveBarClosed">
          <Icon icon="app" />
        </RouteLink>
      </li>
      <li class="nav-item">
        <RouteLink :route="'updates'" :routeLabel="'Atualizações'" :barClosed="effectiveBarClosed">
          <Icon icon="tag" />
        </RouteLink>
      </li>
      <li v-if="showNpsSurveysNav" class="nav-item">
        <RouteLink :route="'nps-surveys'" :routeLabel="'Pesquisa NPS'" :barClosed="effectiveBarClosed">
          <Icon icon="search-nps" />
        </RouteLink>
      </li>
      <li
        v-if="showAcademyHubNav"
        class="nav-item"
      >
        <RouteLink :route="'academy-hub'" :routeLabel="'AcademyHub'" :barClosed="effectiveBarClosed">
          <Icon icon="graph" />
        </RouteLink>
      </li>
      <li
        v-if="showServersNav"
        class="nav-item nav-item--has-submenu"
        @mouseenter="openSubmenu($event, 'servers')"
        @mouseleave="scheduleCloseSubmenu"
      >
        <RouteLink :route="'servers'" :routeLabel="'Servidores'" :barClosed="effectiveBarClosed" @click.native="handleNavClick">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 7C5 5.89543 5.89543 5 7 5H17C18.1046 5 19 5.89543 19 7V9C19 10.1046 18.1046 11 17 11H7C5.89543 11 5 10.1046 5 9V7Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5 15C5 13.8954 5.89543 13 7 13H17C18.1046 13 19 13.8954 19 15V17C19 18.1046 18.1046 19 17 19H7C5.89543 19 5 18.1046 5 17V15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="8" cy="8" r="1" fill="currentColor"/>
            <circle cx="8" cy="16" r="1" fill="currentColor"/>
          </svg>
        </RouteLink>
      </li>
      <li class="nav-item">
        <RouteLink :route="'proximos-eventos'" :routeLabel="'Próximos eventos'" :barClosed="effectiveBarClosed">
          <Icon icon="clock-calendar" />
        </RouteLink>
      </li>
      <template v-if="showCommercialNav">
        <p v-if="!effectiveBarClosed" class="division-title division-title--section-start"
            :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']">
            COMERCIAL</p>
        <p v-else class="division-line division-line--section-start"
            :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']"
            style="color: var(--surface-tertiary);"></p>
        <li class="nav-item nav-item--section-end">
          <RouteLink :route="'commercial-leads'" :routeLabel="'Leads'" :barClosed="effectiveBarClosed">
            <Icon icon="client" />
          </RouteLink>
        </li>
      </template>
      <template v-if="permissions.carteiras(user.position, user.modules && user.modules.wallet === true)">
        <p v-if="!effectiveBarClosed" class="division-title division-title--section-start"
            :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']">
            RELACIONAMENTO</p>
        <p v-else class="division-line division-line--section-start"
            :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']"
            style="color: var(--surface-tertiary);"></p>
        <li class="nav-item nav-item--section-end">
          <RouteLink :route="'carteiras'" :routeLabel="'Carteiras'" :barClosed="effectiveBarClosed">
            <Icon icon="user-historic" />
          </RouteLink>
        </li>
      </template>
      <template
        v-if="
          permissions.suporteDashboard(user.position, user.modules && user.modules.supportDashboard === true) ||
          permissions.chamados(user.position, user.modules && user.modules.bugReport === true)
        "
      >
      <p v-if="!effectiveBarClosed" class="division-title division-title--section-start"
          :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']">
          SUPORTE</p>
      <p v-else class="division-line division-line--section-start"
          :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']"
          style="color: var(--surface-tertiary);"></p>
      <li
        v-if="permissions.suporteDashboard(user.position, user.modules && user.modules.supportDashboard === true)"
        class="nav-item"
        :class="{ 'nav-item--section-end': !permissions.chamados(user.position, user.modules && user.modules.bugReport === true) }"
      >
        <RouteLink :route="'suporte-dashboard'" :routeLabel="'Dashboard'" :barClosed="effectiveBarClosed">
          <Icon icon="graph" />
        </RouteLink>
      </li>
      <li
        v-if="permissions.chamados(user.position, user.modules && user.modules.bugReport === true)"
        class="nav-item nav-item--section-end"
      >
        <RouteLink :route="'chamados'" :routeLabel="'Chamados'" :barClosed="effectiveBarClosed">
          <Icon icon="headset" />
        </RouteLink>
      </li>
      </template>
      <template v-if="showMarketingSection">
        <p v-if="!effectiveBarClosed" class="division-title division-title--section-gap"
            :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']">
            MARKETING</p>
        <p v-else class="division-line division-line--section-gap"
          :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']"
          style="color: var(--surface-tertiary);"></p>
        <li
          v-if="showMarketingLeadsNav"
          class="nav-item"
          :class="{ 'nav-item--section-end': !showMarketingLandingNav && !showMarketingBusinessPlanNav && !showMarketingCampaignsNav && !showMoneyHubNav && !showCouponsNav }"
        >
          <RouteLink :route="'marketing-leads'" :routeLabel="'Leads'" :barClosed="effectiveBarClosed">
            <Icon icon="userplus" />
          </RouteLink>
        </li>
        <li
          v-if="showMarketingLandingNav"
          class="nav-item"
          :class="{ 'nav-item--section-end': !showMarketingBusinessPlanNav && !showMarketingCampaignsNav && !showMoneyHubNav && !showCouponsNav }"
        >
          <RouteLink :route="'marketing-landing-form'" :routeLabel="'Landing page'" :barClosed="effectiveBarClosed">
            <Icon icon="graph" />
          </RouteLink>
        </li>
        <li
          v-if="showMarketingCampaignsNav"
          class="nav-item"
          :class="{ 'nav-item--section-end': !showMarketingBusinessPlanNav && !showMoneyHubNav && !showCouponsNav }"
        >
          <RouteLink :route="'marketing-campaigns'" :routeLabel="'Modais internos'" :barClosed="effectiveBarClosed">
            <Icon icon="desktop" />
          </RouteLink>
        </li>
        <li
          v-if="showMarketingBusinessPlanNav"
          class="nav-item"
          :class="{ 'nav-item--section-end': !showMoneyHubNav && !showCouponsNav }"
        >
          <RouteLink :route="'marketing-business-plan'" :routeLabel="'Business Plan'" :barClosed="effectiveBarClosed">
            <Icon icon="bullseye" />
          </RouteLink>
        </li>
        <li
          v-if="showMoneyHubNav"
          class="nav-item"
          :class="{ 'nav-item--section-end': !showCouponsNav }"
        >
          <RouteLink
            :route="moneyHubRoute"
            :active-routes="moneyHubActiveRoutes"
            :routeLabel="'MoneyHub'"
            :barClosed="effectiveBarClosed"
          >
            <Icon icon="money-arrow" />
          </RouteLink>
        </li>
        <li v-if="showCouponsNav" class="nav-item nav-item--section-end">
          <RouteLink :route="'coupons'" :routeLabel="'Cupons'" :barClosed="effectiveBarClosed">
            <Icon icon="tag" />
          </RouteLink>
        </li>
      </template>
      <template v-if="permissions.master(user.position)">
        <p v-if="!effectiveBarClosed"
            class="division-title division-title--section-gap"
            :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']">
            MASTER</p>
        <p v-else
          class="division-line division-line--section-gap"
          :class="[effectiveBarClosed ? 'shrink-division-title' : 'expand-division-title']"
          style="color: var(--surface-tertiary);"></p>
        <li class="nav-item">
          <RouteLink :route="'master-logs'" :routeLabel="'Histórico'" :barClosed="effectiveBarClosed">
            <Icon icon="clock" />
          </RouteLink>
        </li>
        <li class="nav-item nav-item--section-end">
          <RouteLink :route="'master-users'" :routeLabel="'Usuários'" :barClosed="effectiveBarClosed">
            <Icon icon="user" />
          </RouteLink>
        </li>
      </template>
    </ul>
    </div>

    <transition name="submenu-fade">
      <div
        v-if="activeSubmenu && !isMobile"
        ref="submenuFlyout"
        class="nav-submenu"
        :style="submenuStyle"
        @mouseenter="cancelCloseSubmenu"
        @mouseleave="scheduleCloseSubmenu"
      >
        <span v-if="submenuTitle" class="nav-submenu-title">{{ submenuTitle }}</span>
        <button
          v-for="item in submenuItems"
          :key="item.key || item.route + (item.query ? JSON.stringify(item.query) : '')"
          class="nav-submenu-item"
          :class="{ 'nav-submenu-item--disabled': item.disabled }"
          :disabled="item.disabled"
          @click="navigateSubmenu(item)"
        >
          <span>{{ item.label }}</span>
          <span v-if="item.isSoon" class="nav-submenu-soon">Em breve</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue'
import RouteLink from './RouteLink.vue';
import Icon from '../ui/Icon.vue';
import { permissions, resolveMarketingLeadsEnabled, resolveLandingPageEnabled, resolveMarketingBusinessPlanEnabled, resolveMarketingCampaignsEnabled, resolveAffiliatesMarketingEnabled, resolveAffiliatesDashboardEnabled, resolveAffiliatesFinanceEnabled, resolveAffiliatesPlansEnabled, resolveAcademyHubEnabled, resolveNpsSurveysEnabled, resolveCancelamentosEnabled, resolveCouponsEnabled, resolveCommercialEnabled, canAccessFinanceRoute, canAccessServersRoute, canAccessAutonomousRoute } from '../../utils/permissions';
import {
  CRM_MODULES,
  CRM_SUBMENU_ROUTE_KEYS,
  resolveSubmoduleEnabled
} from '../../pages/master/constants/crmModuleCatalog';

export default {
  components: {
    RouteLink,
    Icon,
  },
  data: () => ({
    permissions,
    user: {},
    sidebarDefault: false,
    barClosed: true,
    mobileMenuOpen: false,
    windowWidth: window.innerWidth,
    isDarkMode: false,
    activeSubmenu: null,
    submenuStyle: {},
    submenuCloseTimer: null,
  }),
  computed: {
    isMobile() {
      return this.windowWidth <= 768;
    },
    effectiveBarClosed() {
      // On mobile, always show expanded menu
      return this.isMobile ? false : this.barClosed;
    },
    marketingLeadsModuleEnabled () {
      return resolveMarketingLeadsEnabled(this.user.modules || {})
    },
    marketingLandingModuleEnabled () {
      return resolveLandingPageEnabled(this.user.modules || {})
    },
    marketingBusinessPlanModuleEnabled () {
      return resolveMarketingBusinessPlanEnabled(this.user.modules || {})
    },
    marketingCampaignsModuleEnabled () {
      return resolveMarketingCampaignsEnabled(this.user.modules || {})
    },
    showMarketingLeadsNav () {
      return permissions.marketingLeads(this.user.position, this.marketingLeadsModuleEnabled)
    },
    showMarketingLandingNav () {
      return permissions.marketingLandingPage(this.user.position, this.marketingLandingModuleEnabled)
    },
    showMarketingBusinessPlanNav () {
      return permissions.marketingBusinessPlan(this.user.position, this.marketingBusinessPlanModuleEnabled)
    },
    showMarketingCampaignsNav () {
      return permissions.marketingCampaigns(this.user.position, this.marketingCampaignsModuleEnabled)
    },
    showMarketingSection () {
      return this.showMarketingLeadsNav || this.showMarketingLandingNav || this.showMarketingBusinessPlanNav || this.showMarketingCampaignsNav || this.showMoneyHubNav || this.showCouponsNav
    },
    showAffiliatesDashboardNav () {
      return permissions.affiliatesDashboard(
        this.user.position,
        resolveAffiliatesDashboardEnabled(this.user.modules || {})
      )
    },
    showAffiliatesFinanceNav () {
      return permissions.affiliatesFinance(
        this.user.position,
        resolveAffiliatesFinanceEnabled(this.user.modules || {})
      )
    },
    showAffiliatesMarketingNav () {
      return permissions.affiliatesMarketing(
        this.user.position,
        resolveAffiliatesMarketingEnabled(this.user.modules || {})
      )
    },
    showAffiliatesPlansNav () {
      return permissions.affiliatesPlans(
        this.user.position,
        resolveAffiliatesPlansEnabled(this.user.modules || {})
      )
    },
    showMoneyHubNav () {
      return this.showAffiliatesDashboardNav || this.showAffiliatesFinanceNav || this.showAffiliatesPlansNav || this.showAffiliatesMarketingNav
    },
    showCouponsNav () {
      return permissions.coupons(
        this.user.position,
        resolveCouponsEnabled(this.user.modules || {})
      )
    },
    showCommercialNav () {
      return permissions.commercial(
        this.user.position,
        resolveCommercialEnabled(this.user.modules || {})
      )
    },
    moneyHubRoute () {
      if (this.showAffiliatesDashboardNav) return 'affiliates-dashboard'
      if (this.showAffiliatesFinanceNav) return 'affiliates'
      if (this.showAffiliatesPlansNav) return 'commission-plans'
      return 'affiliates-marketing'
    },
    moneyHubActiveRoutes () {
      return ['affiliates-dashboard', 'affiliates', 'commission-plans', 'affiliates-marketing']
    },
    showAcademyHubNav () {
      return permissions.academyHub(
        this.user.position,
        resolveAcademyHubEnabled(this.user.modules || {})
      )
    },
    showNpsSurveysNav () {
      return permissions.npsSurveys(
        this.user.position,
        resolveNpsSurveysEnabled(this.user.modules || {})
      )
    },
    showFinanceNav () {
      return canAccessFinanceRoute(this.user.position, this.user.modules || {})
    },
    showAutonomousNav () {
      return canAccessAutonomousRoute(this.user.position, this.user.modules || {})
    },
    autonomousRoute () {
      const first = this.autonomousSubmenuItems[0]
      return first ? first.route : 'master-autonomous-upsells'
    },
    showCancelamentosNav () {
      return permissions.cancelamentos(
        this.user.position,
        resolveCancelamentosEnabled(this.user.modules || {})
      )
    },
    showAplicativosNav () {
      const modules = this.user.modules || {}
      return permissions.aplicativos(this.user.position, modules.aplicativos === true)
    },
    showServersNav () {
      return canAccessServersRoute(this.user.position, this.user.modules || {})
    },
    usersSubmenuItems() {
      const items = []
      const position = this.user.position
      const pushIf = (tab, label, legacyVisible) => {
        if (this.shouldShowSubmenuItem('users', tab, legacyVisible)) {
          items.push({ route: 'users', query: { tab }, label })
        }
      }
      const modules = this.user.modules || {}
      pushIf('dashboard', 'Dashboard', permissions.clientsDashboard(position, modules.clientsDashboard === true))
      pushIf('geral', 'Geral', true)
      if (position !== 'outbound') {
        pushIf('cancelados', 'Cancelados', true)
      }
      pushIf('score', 'Score', permissions.clientsScore(position, modules.clientsScore === true))
      pushIf('upsell', 'Upsell', permissions.clientsUpsell(position, modules.clientsUpsell === true))
      return items
    },
    financeSubmenuItems () {
      const items = []
      const pushIf = (tab, label, legacyVisible) => {
        if (this.shouldShowSubmenuItem('finance', tab, legacyVisible)) {
          items.push({ route: 'finance', query: { tab }, label })
        }
      }
      pushIf('contratos', 'Contratos', true)
      pushIf('excedentes', 'Excedentes', true)
      return items
    },
    serversSubmenuItems() {
      const items = []
      const position = this.user.position
      const pushIf = (tab, label, legacyVisible) => {
        if (this.shouldShowSubmenuItem('servers', tab, legacyVisible)) {
          items.push({ route: 'servers', query: { tab }, label })
        }
      }
      pushIf('servidores', 'Servidores', permissions.master(position))
      pushIf('connect', 'Servidor Connect', false)
      pushIf('processos', 'Processos', false)
      pushIf('xml', 'Fila de XML', false)
      pushIf('firebase', 'Custos Firebase', false)
      return items
    },
    autonomousSubmenuItems () {
      const modules = this.user.modules || {}
      const position = this.user.position
      const items = []
      if (permissions.autonomousUpsells(position, modules.autonomousUpsells === true)) {
        items.push({ route: 'master-autonomous-upsells', label: 'Upsells' })
      }
      if (permissions.autonomousSelfService(position, modules.autonomousSelfService === true)) {
        items.push({ route: 'master-autonomous-self-service', label: 'Self-service' })
      }
      return items
    },
    masterLogsSubmenuItems () {
      const items = []
      const position = this.user.position
      const pushIf = (tab, label, legacyVisible) => {
        if (this.shouldShowSubmenuItem('master-logs', tab, legacyVisible)) {
          items.push({ route: 'master-logs', query: { tab }, label })
        }
      }
      pushIf('dashboard', 'Dashboard', permissions.master(position))
      pushIf('logs', 'Logs', permissions.master(position))
      return items
    },
    cancelamentosSubmenuItems() {
      return [
        { route: 'cancelamentos', query: { tab: 'dashboard' }, label: 'Dashboard' },
        { route: 'cancelamentos', query: { tab: 'cancelamentos' }, label: 'Cancelamentos' },
        { route: 'cancelamentos', query: { tab: 'logs' }, label: 'Logs' },
      ]
    },
    submenuItems() {
      const menus = {
        users: this.usersSubmenuItems,
        finance: this.financeSubmenuItems,
        cancelamentos: this.cancelamentosSubmenuItems,
        servers: this.serversSubmenuItems,
        history: this.masterLogsSubmenuItems,
        autonomos: this.autonomousSubmenuItems,
      }
      return menus[this.activeSubmenu] || []
    },
    submenuTitle() {
      const titles = {
        users: 'Clientes',
        finance: 'Financeiro',
        cancelamentos: 'Cancelamentos',
        servers: 'Servidores',
        history: 'Histórico',
        autonomos: 'Autônomos',
      }
      return titles[this.activeSubmenu] || ''
    },
  },
  watch: {
    '$route'() {
      this.activeSubmenu = null
      // Close mobile menu on route change
      if (this.mobileMenuOpen) {
        this.closeMobileMenu()
      }
    }
  },
  mounted () {
    this.getData()
    this.$root.$on('verifyActivated', this.verifyActivated)
    this.$root.$on('Sidebar::toggleMobile', this.toggleMobileMenu)
    window.addEventListener('resize', this.handleResize)
    this.syncThemeFromStorage()
    this._onThemeChanged = () => { this.syncThemeFromStorage() }
    window.addEventListener('hub:theme-changed', this._onThemeChanged)
  },
  beforeDestroy () {
    this.cancelCloseSubmenu()
    this.$root.$off('Sidebar::toggleMobile', this.toggleMobileMenu)
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('hub:theme-changed', this._onThemeChanged)
  },
  methods: {
    shouldShowSubmenuItem (route, tab, legacyVisible) {
      const modules = this.user.modules || {}
      const position = this.user.position
      const mapKey = `${route}:${tab}`
      const moduleKey = CRM_SUBMENU_ROUTE_KEYS[mapKey]
      if (!moduleKey) return legacyVisible
      const entry = CRM_MODULES.find((m) => m.key === moduleKey)
      if (!entry || !entry.available) return legacyVisible
      return permissions.master(position) || resolveSubmoduleEnabled(modules, moduleKey)
    },
    handleResize() {
      this.windowWidth = window.innerWidth
      if (!this.isMobile && this.mobileMenuOpen) {
        this.mobileMenuOpen = false
      }
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    handleNavClick() {
      if (this.mobileMenuOpen) {
        this.closeMobileMenu()
      }
    },
    openSubmenu(event, menuName) {
      if (this.isMobile) return
      this.cancelCloseSubmenu()
      const li = event.currentTarget
      const rect = li.getBoundingClientRect()
      this.activeSubmenu = menuName
      this.submenuStyle = {
        top: rect.top + 'px',
        left: (rect.right + 8) + 'px',
      }
      this.$nextTick(() => {
        this.adjustSubmenuPosition(rect)
      })
    },
    adjustSubmenuPosition(anchorRect) {
      const el = this.$refs.submenuFlyout
      if (!el) return
      const menuRect = el.getBoundingClientRect()
      const viewportPadding = 12
      const maxBottom = window.innerHeight - viewportPadding
      let top = anchorRect.top
      if (menuRect.bottom > maxBottom) {
        top = anchorRect.bottom - menuRect.height
      }
      if (top < viewportPadding) {
        top = viewportPadding
      }
      this.submenuStyle = {
        top: top + 'px',
        left: (anchorRect.right + 8) + 'px',
      }
    },
    scheduleCloseSubmenu() {
      this.submenuCloseTimer = setTimeout(() => {
        this.activeSubmenu = null
      }, 80)
    },
    cancelCloseSubmenu() {
      if (this.submenuCloseTimer) {
        clearTimeout(this.submenuCloseTimer)
        this.submenuCloseTimer = null
      }
    },
    navigateSubmenu(item) {
      if (item.disabled) return
      this.activeSubmenu = null
      const navTarget = { name: item.route }
      if (item.query) navTarget.query = item.query
      this.$router.push(navTarget).catch(() => {})
      this.handleNavClick()
    },
    async changeSidebar () {
      this.$emit('handleChange')
      this.sidebarDefault = false
      this.barClosed = !this.barClosed
    },
    async getData () {
      await this.getUser()
    },
    async doLogout () {
      this.$root.$emit('Spinner::show')
      await this.$firebase.auth().signOut()
      this.$router.push({ name: 'login' })
      this.$root.$emit('Spinner::hide')
    },
    async getUser () {
      const snapshot = await this.$firebase.database().ref(`support/supportAffiliates/${window.uid}`).once('value')
      this.user = snapshot.val() || {}
      this.$root.$emit('verifyActivated')
    },
    verifyActivated () {
      if (this.user.activated === false) {
        this.doLogout()
        Vue.prototype.$activated = false
      }
      Vue.prototype.$activated = true
    },
    syncThemeFromStorage () {
      const savedTheme = localStorage.getItem('theme')
      if (savedTheme) {
        this.isDarkMode = savedTheme === 'dark'
        return
      }
      this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
    },
  }
}
</script>

<style lang="scss" scoped>
.navigation-wrapper {
  position: relative;
  height: 100%;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    overflow: visible;
    z-index: 1200;
    pointer-events: none;

    .mobile-overlay,
    .navigation-sidebar {
      pointer-events: auto;
    }
  }
}

.sidebar-toggle-btn {
  position: absolute;
  top: 50%;
  left: 226px;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--stroke-contrast);
  background-color: var(--surface-primary);
  color: var(--color-slate-400);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: left 0.6s ease, background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: var(--surface-tertiary);
    color: var(--text-color-primary);
  }

  &.rotated {
    left: 70px;

    svg {
      transform: rotate(180deg);
    }
  }

  svg {
    transition: transform 0.3s ease;
  }
}

.col-lg-2{
  padding: 0;
  margin: 0;
}
.left-icon{
  position: absolute;
  transform: rotateX(180deg) rotateY(180deg) rotate(270deg) translate(3px, 27px);
}
.dropdown-box{
  margin: 0 2px 0px 28px;
  padding-bottom: 15px;
  background-color: #fafafa;
  border-radius: 5px;
  font-size: 13.7px !important;
  font-weight: 400 !important;
  // &:not(.activated) li a{
  //   transform: translate(-200px, 0) !important;
  //   transition: all .3s !important;
  // }
  &.activated li a{
    transform: translate(21px, 0) !important;
    transition: all .3s !important;
    border-left: 1px solid #e1e1e1;
    border-radius: 0px !important;
    padding-left: 32px !important;
  }
}
.navigation-sidebar{
  height: 100%;
  overflow: hidden auto;
  width: 240px;
  box-shadow: none;
  background-color: var(--surface-primary);
  z-index: 0;
  border-right: 2px solid var(--stroke-contrast);
  padding-top: 16px;
  flex-shrink: 0;
  position: relative;

  &::-webkit-scrollbar {
    display: none !important;
  }

  .division-title {
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 500;
    margin: 0 0 8px 16px;

    &.division-title--section-start {
      margin-top: 12px;
    }

    &.division-title--section-gap {
      margin-top: 16px;
    }

    &.shrink-division-title {
      animation: shrinkDivisionTitle 0.6s forwards;
    }

    &.expand-division-title {
      animation: expandDivisionTitle 0.6s forwards;
    }
  }

  &.shrink-sidebar {
    animation: shrink 0.6s forwards;
  }

  &.expand-sidebar {
    animation: expand 0.6s forwards;
  }

  .division-line {
    width: 40px;
    height: 2px;
    background-color: var(--stroke-contrast);
    margin: 8px 0 25.5px 21px;

    &.division-line--section-start {
      margin-top: 12px;
    }

    &.division-line--section-gap {
      margin-top: 16px;
    }
  }

  .nav-item--section-end {
    margin-bottom: 16px;
  }

  .icon-ajust{
    margin-left: 2px;
    margin-right: 10px;
  }
  li{
    margin: 3.5px 5px;
    letter-spacing: .25px;
  }
}
.img-logo {
  width: 153px;
  transform: translate(-28px, 0px);
}
.questionsIcon {
  width: 50px;
  height: 50px;
}
// Componente 'navigation-sidebar'
@keyframes shrink {
  from {
    width: 240px;
  }

  to {
    width: 84px;
  }
}

@keyframes expand {
  from {
    width: 84px;
  }

  to {
    width: 240px;
  }
}

// Componente 'divisor-container'
@keyframes shrinkLine {
  from {
    transform: none;
    left: 190px;
  }

  to {
    left: 48px;
    transform: rotate(-180deg);
  }
}

// Componente 'division-title'
@keyframes shrinkDivisionTitle {
  from {
    margin-left: 16px;
  }

  to {
    margin-left: 20px;
  }
}

@keyframes expandDivisionTitle {
  from {
    margin-left: 20px;
  }

  to {
    margin-left: 16px;
  }
}

@keyframes expandLine {
  from {
    left: 48px;
  }

  to {
    left: 190px;
  }
}

@keyframes hidingNotification {
  0% {
    display: none;
  }

  50% {
    opacity: 0;
    display: none;
  }

  100% {
    opacity: 0;
    display: none;
  }
}

@keyframes showText {
  0% {
    opacity: 0;
    display: none;
  }

  25% {
    opacity: 0;
    display: none;
  }

  50% {
    opacity: 0;
    display: none;
  }

  75% {
    opacity: 0.6;
  }

  100% {
    opacity: 1;
  }
}

// Hamburger button styles (handled by TopBar)
.hamburger-btn {
  display: none;
}

// Mobile overlay
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  &.is-visible {
    opacity: 1;
    visibility: visible;
  }
}

// Mobile responsive styles
@media screen and (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }

  .navigation-sidebar {
    position: fixed;
    top: 0;
    left: -280px;
    width: 280px !important;
    height: 100vh;
    height: 100dvh;
    z-index: 1201;
    transition: left 0.3s ease;
    box-shadow: none;
    padding-top: env(safe-area-inset-top);

    &.mobile-open {
      left: 0;
      box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
    }

    // Force expanded state on mobile
    &.shrink-sidebar {
      animation: none;
      width: 240px !important;
    }

    .division-title {
      display: block !important;
      opacity: 1 !important;
      
      &.shrink-division-title {
        animation: none;
        margin-left: 16px;
      }
    }
  }
}

.nav-item--has-submenu {
  position: relative;
}

.nav-submenu {
  position: fixed;
  min-width: 220px;
  background: var(--surface-primary);
  border: 1px solid var(--stroke-contrast);
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1300;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-submenu-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 8px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-brand);
  border-bottom: 1px solid var(--stroke-contrast);
  margin-bottom: 4px;
}

.nav-submenu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: none;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;
  text-align: left;
  width: 100%;

  &:hover:not(:disabled) {
    background: var(--surface-tertiary);
  }

  &--disabled,
  &:disabled {
    opacity: 0.72;
    cursor: not-allowed;
  }
}

.nav-submenu-soon {
  display: inline-flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: center;
  color: var(--text-invert, #fff);
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: -0.13px;
  border-radius: 4px;
  background: var(--surface-light-contrast);
  flex-shrink: 0;
}

.submenu-fade-enter-active,
.submenu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.submenu-fade-enter,
.submenu-fade-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}

// Tablet adjustments
@media screen and (max-width: 1024px) and (min-width: 769px) {
  .navigation-sidebar {
    width: 84px;
  }

  .sidebar-toggle-btn {
    left: 70px;

    &:not(.rotated) {
      left: 70px;
    }
  }
}
</style>

<style lang="scss">
// Estilos globais para ícones ativos no menu (sem scoped para penetrar nos componentes filhos)
.navigation-sidebar {
  .nav-link.active {
    .icon-container {
      color: var(--icon-brand);
      
      svg {
        color: var(--icon-brand);
        
        path {
          stroke: var(--icon-brand);
        }
        
        circle {
          fill: var(--icon-brand);
        }
      }
    }
  }
  
  .nav-link:hover {
    .icon-container {
      color: var(--icon-brand);
      
      svg {
        color: var(--icon-brand);
        
        path {
          stroke: var(--icon-brand);
        }
      }
    }
  }
}
</style>
